import React from 'react'
import type { MarketplaceNotification } from '../../../services/backendService'
import '../styles/MarketplaceInbox.css'

interface MarketplaceInboxProps {
  title: string
  notifications: MarketplaceNotification[]
  unreadCount: number
  isLoading: boolean
  errorMessage?: string | null
  markingNotificationId?: string | null
  onRefresh?: () => void | Promise<void>
  onMarkAsRead?: (notificationId: string) => void | Promise<void>
  onOpenJob?: (jobId: string) => void
}

const formatDateTime = (value: string) => new Date(value).toLocaleString()

const unreadLabel = (unreadCount: number) => (
  unreadCount === 1 ? '1 unread message' : `${unreadCount} unread messages`
)

export const MarketplaceInbox: React.FC<MarketplaceInboxProps> = ({
  title,
  notifications,
  unreadCount,
  isLoading,
  errorMessage,
  markingNotificationId,
  onRefresh,
  onMarkAsRead,
  onOpenJob
}) => {
  const [viewMode, setViewMode] = React.useState<'card' | 'list'>('card')
  const unreadNotifications = notifications.filter(notification => !notification.isRead)
  const readNotifications = notifications.filter(notification => notification.isRead)

  const renderNotificationSection = (sectionTitle: string, items: MarketplaceNotification[], sectionClassName: string) => {
    if (items.length === 0) {
      return null
    }

    return (
      <div className="marketplace-inbox-section">
        <h3>{sectionTitle} ({items.length})</h3>
        <div className={`marketplace-inbox-grid ${viewMode !== 'card' ? 'marketplace-inbox-list-view' : ''}`}>
          {items.map(notification => {
            const jobTitle = typeof notification.metadata?.jobTitle === 'string' ? notification.metadata.jobTitle : null
            const farmName = typeof notification.metadata?.farmName === 'string' ? notification.metadata.farmName : null
            const status = typeof notification.metadata?.status === 'string' ? notification.metadata.status : null
            const jobId = typeof notification.metadata?.jobId === 'string' ? notification.metadata.jobId : null

            return (
              <div
                key={notification.id}
                className={`marketplace-inbox-card ${sectionClassName} ${viewMode !== 'card' ? 'list-view compact-view' : ''}`.trim()}
              >
                <div className="marketplace-inbox-card-header">
                  <div>
                    <h4>{notification.title}</h4>
                    <p className="marketplace-inbox-card-date">{formatDateTime(notification.createdAt)}</p>
                  </div>
                  {!notification.isRead && <span className="marketplace-inbox-badge">New</span>}
                </div>

                <p className="marketplace-inbox-card-message">{notification.message}</p>

                <div className="marketplace-inbox-meta">
                  {jobTitle && <span>Job: {jobTitle}</span>}
                  {farmName && <span>Farm: {farmName}</span>}
                  {status && <span>Status: {status.replace(/_/g, ' ')}</span>}
                </div>

                <div className="marketplace-inbox-card-actions">
                  {jobId && onOpenJob && (
                    <button
                      type="button"
                      className="marketplace-inbox-button"
                      onClick={() => onOpenJob(jobId)}
                    >
                      Open job →
                    </button>
                  )}
                  {!notification.isRead && onMarkAsRead && (
                    <button
                      type="button"
                      className="marketplace-inbox-button secondary"
                      onClick={() => onMarkAsRead(notification.id)}
                      disabled={markingNotificationId === notification.id}
                    >
                      {markingNotificationId === notification.id ? 'Marking…' : 'Mark as read'}
                    </button>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  return (
    <div className="marketplace-inbox">
      <div className="marketplace-inbox-header">
        <div>
          <h2>{title}</h2>
          <p className="marketplace-inbox-summary">{unreadLabel(unreadCount)}</p>
        </div>
        <div className="marketplace-inbox-controls">
          <div className="marketplace-inbox-view-toggle" aria-label="Inbox view mode">
            <button
              type="button"
              className={`marketplace-inbox-view-toggle-btn ${viewMode === 'card' ? 'is-active' : ''}`}
              onClick={() => setViewMode('card')}
            >
              Cards
            </button>
            <button
              type="button"
              className={`marketplace-inbox-view-toggle-btn ${viewMode === 'list' ? 'is-active' : ''}`}
              onClick={() => setViewMode('list')}
            >
              List
            </button>
          </div>

          {onRefresh && (
            <button type="button" className="marketplace-inbox-button secondary" onClick={() => void onRefresh()}>
              Refresh
            </button>
          )}
        </div>
      </div>

      {errorMessage && <div className="marketplace-error-banner">{errorMessage}</div>}

      {isLoading ? (
        <div className="empty-state">
          <p>Loading inbox messages...</p>
        </div>
      ) : notifications.length === 0 ? (
        <div className="empty-state">
          <p>No marketplace messages yet.</p>
        </div>
      ) : (
        <>
          {renderNotificationSection('Unread', unreadNotifications, 'unread')}
          {renderNotificationSection('Read', readNotifications, 'read')}
        </>
      )}
    </div>
  )
}